import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useEffect,
  useState,
} from "react";
import * as Yup from "yup";

interface ChecklistItem {
  name: string;
  isRequired: boolean;
  isDocumentRequired: boolean;
}

const emptyItem: ChecklistItem = {
  name: "",
  isRequired: false,
  isDocumentRequired: false,
};

const checklistSchema = Yup.object({
  name: Yup.string().trim().required("Checklist name is required"),
  isRequired: Yup.boolean().required(),
  isDocumentRequired: Yup.boolean().required(),
});

const RequiredOnBordingChecklist: ForwardRefRenderFunction<any, any> = (
  props,
  ref,
) => {
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [isAllowed, setIsAllowed] = useState(false);
  const [draft, setDraft] = useState<ChecklistItem>(emptyItem);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const checklist = (props?.data?.requiredOnBordingChecklist || []).map(
      (item: ChecklistItem) => ({
        name: item?.name || "",
        isRequired: item?.isRequired === true,
        isDocumentRequired: item?.isDocumentRequired === true,
      }),
    );
    setItems(checklist);
    const allowed = props?.data?.isAllowedOnBordingSubmission === true;
    setIsAllowed(allowed);
    props?.HandleData({
      isAllowedOnBordingSubmission: allowed,
      requiredOnBordingChecklist: checklist,
    });
  }, [props?.data]);

  const updateItems = (nextItems: ChecklistItem[]) => {
    setItems(nextItems);
    props?.HandleData({
      isAllowedOnBordingSubmission: isAllowed,
      requiredOnBordingChecklist: nextItems,
    });
  };

  const saveDraft = async () => {
    try {
      const validDraft = {
        ...draft,
        name: draft.name.trim(),
      };
      await checklistSchema.validate(validDraft);
      const nextItems = [...items];
      if (editingIndex === null) {
        nextItems.push(validDraft);
      } else {
        nextItems[editingIndex] = validDraft;
      }
      updateItems(nextItems);
      setDraft(emptyItem);
      setEditingIndex(null);
      setError("");
    } catch (validationError: any) {
      setError(validationError.message);
    }
  };

  const editItem = (index: number) => {
    setDraft({ ...items[index] });
    setEditingIndex(index);
    setError("");
  };

  const cancelEdit = () => {
    setDraft(emptyItem);
    setEditingIndex(null);
    setError("");
  };

  const deleteItem = (index: number) => {
    updateItems(items.filter((_, itemIndex) => itemIndex !== index));
    if (editingIndex === index) {
      cancelEdit();
    }
  };

  const validateData = async () => {
    const invalidItem = items.find((item) => !item.name.trim());
    if (invalidItem) {
      setError("Checklist name is required");
      return false;
    }
    props?.HandleData({
      isAllowedOnBordingSubmission: isAllowed,
      requiredOnBordingChecklist: items.map((item) => ({
        name: item.name.trim(),
        isRequired: item.isRequired === true,
        isDocumentRequired: item.isDocumentRequired === true,
      })),
    });
    setError("");
    return true;
  };

  React.useImperativeHandle(ref, () => ({ validateData }));

  return (
    <div className="space-y-5">
      <label className="flex items-center gap-2 text-sm font-semibold">
        <input
          type="checkbox"
          checked={isAllowed}
          onChange={(event) => {
            const allowed = event.target.checked;
            setIsAllowed(allowed);
            props?.HandleData({
              isAllowedOnBordingSubmission: allowed,
              requiredOnBordingChecklist: items,
            });
          }}
        />
        Activate Onboarding Checklist?
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label
            className="block text-sm font-semibold mb-1"
            htmlFor="checklist-name"
          >
            Onboarding task name
          </label>
          <input
            id="checklist-name"
            value={draft.name}
            onChange={(event) =>
              setDraft({ ...draft, name: event.target.value })
            }
            className="border border-neutral-300 rounded-lg h-10 px-3 w-full"
            placeholder="Enter onboarding task name"
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.isRequired}
            onChange={(event) =>
              setDraft({ ...draft, isRequired: event.target.checked })
            }
          />
          Required
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.isDocumentRequired}
            onChange={(event) =>
              setDraft({ ...draft, isDocumentRequired: event.target.checked })
            }
          />
          Document required
        </label>
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={saveDraft}
          className="bg-[#0078ae] text-white px-4 py-2 rounded-lg font-semibold"
        >
          {editingIndex === null
            ? "Add onboarding task"
            : "Update onboarding task"}
        </button>
        {editingIndex !== null && (
          <button
            type="button"
            onClick={cancelEdit}
            className="border border-neutral-300 px-4 py-2 rounded-lg"
          >
            Cancel
          </button>
        )}
      </div>
      <div className="space-y-2">
        {items.length ? (
          items.map((item, index) => (
            <div
              key={`${item.name}-${index}`}
              className="border border-neutral-200 rounded-lg p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
            >
              <div>
                <div className="font-semibold">{item.name}</div>
                <div className="text-sm text-neutral-600">
                  Required: {item.isRequired ? "Yes" : "No"}{" "}
                  <span className="mx-2">|</span>
                  Document required: {item.isDocumentRequired ? "Yes" : "No"}
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => editItem(index)}
                  className="text-[#0078ae] font-semibold"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => deleteItem(index)}
                  className="text-red-500 font-semibold"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="text-sm text-neutral-500">
            No onboarding tasks added.
          </div>
        )}
      </div>
    </div>
  );
};

export default forwardRef(RequiredOnBordingChecklist);
